import React, { useCallback, useContext, useState } from 'react';
import Modal from '@atlaskit/modal-dialog';
import styled from 'styled-components';

import HighchartStyles from 'components/HighchartStyles';
import Tooltip from 'components/Tooltip';
import AnalyticsContext from 'contexts/AnalyticsContext';
import CardContext from 'contexts/CardContext';
import Button from 'kingpin/atoms/Button';

// Relative: a bare 'constants' resolves to Node's built-in module
import { CANVAS_VERTICAL_SNAPPING_POINTS } from '../../constants';

interface GadgetFullscreenContextType {
  // Undefined outside a provider, e.g. gadget builder previews
  open?: () => void;
  isFullscreen: boolean;
}

export const GadgetFullscreenContext =
  React.createContext<GadgetFullscreenContextType>({
    isFullscreen: false,
  });

export const useGadgetFullscreen = () => useContext(GadgetFullscreenContext);

const Body = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
`;

// Over the header's right-hand side, where the hidden hover buttons sit
const CloseButton = styled.div`
  position: absolute;
  top: 4px;
  right: 8px;
  z-index: 1;
`;

// The modal is capped to the window less atlaskit's gutters
const FULLSCREEN_SIZE = { width: '100vw', height: '100vh' };

// Lets a dashboard or report gadget open over the whole window. The
// fullscreen copy renders inside the same providers as the card, so it
// shares the card's dates, comparison and drill-downs, and the modal's portal
// still receives them.
const GadgetFullscreenProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { trackEvent } = useContext(AnalyticsContext);
  const card = useContext(CardContext);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const open = useCallback(() => {
    setIsOpen(true);
    trackEvent('Gadget - Opened Fullscreen');
  }, [trackEvent]);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <GadgetFullscreenContext.Provider value={{ open, isFullscreen: false }}>
      {children}
      {isOpen && (
        <Modal
          width={FULLSCREEN_SIZE.width}
          height={FULLSCREEN_SIZE.height}
          onClose={close}
          shouldScrollInViewport={false}
          autoFocus={false}
          label={card.chartDefinition?.name || 'Fullscreen gadget'}
          testId="gadget-fullscreen"
        >
          <GadgetFullscreenContext.Provider value={{ isFullscreen: true }}>
            {/* Sized as a full screen of the grid, so headline numbers and
                charts lay out as they would on a tile that big */}
            <CardContext.Provider
              value={{ ...card, w: 12, h: CANVAS_VERTICAL_SNAPPING_POINTS }}
            >
              <HighchartStyles style={{ display: 'flex', height: '100%' }}>
                <Body>
                  {children}
                  <CloseButton>
                    <Tooltip content="Close full screen" position="left">
                      <Button
                        type="Ghost"
                        size="Small"
                        icon="cross"
                        onClick={close}
                        testId="gadget-fullscreen-close"
                      />
                    </Tooltip>
                  </CloseButton>
                </Body>
              </HighchartStyles>
            </CardContext.Provider>
          </GadgetFullscreenContext.Provider>
        </Modal>
      )}
    </GadgetFullscreenContext.Provider>
  );
};

export default GadgetFullscreenProvider;
